import _ from "underscore";
import hosAddressTemplate from "./hosAddress.tmpl";
import logAddressTemplate from "../addressDialog.tmpl";
import { Dialog } from "../dialog";
import Translator from "../../../utils/translator";
import { hasKnownDevice } from "../../../sync/common";
import { getServerDate } from "../../../utils/storage/date";
import DutyStatusLog, { IAddress } from "../../../model/DutyStatusLog";
import Sync from "../../../sync/sync";
import * as logger from "../../../utils/logger";
import { hide, htmlEscape, show } from "../../../utils/vanilla";

export interface IHosAddressTemplateOptions {
    id: string;
    address: string | undefined;
    isRequestedEdit: boolean;
    hasAddress: boolean;
}

export class HosAddress {
    private readonly logAddressInputTemplate = _.template(logAddressTemplate);
    private readonly translator: Translator;
    private readonly sync: Sync;

    constructor(translator: Translator, sync: Sync) {
        this.translator = translator;
        this.sync = sync;
    }

    validateAddressInput(...addressParts: string[]) {
        let fullAddress = addressParts.join("");
        return (fullAddress.length >= 5 && fullAddress.length <= 60);
    }

    getHtmlTemplate(options: IHosAddressTemplateOptions) {
        return _.template(hosAddressTemplate)({
            id: options.id,
            address: options.address,
            isRequestedEdit: options.isRequestedEdit,
            hasAddress: options.hasAddress
        });
    }

    handleAddressButtonClick(
        log: DutyStatusLog | null,
        currentUser: DeepReadonly<IUser> | null,
        isEWD: boolean,
        isNightMode: boolean,
        populateChanges: (forceRender?: boolean) => Promise<void>,
        updateNavigationView: () => void
    ) {
        this.hideAddressWarning();
        let addressDialog = new Dialog(
            "hos-address-input",
            this.logAddressInputTemplate({ isAustralia: isEWD }),
            this.translator,
            "Enter an address",
            [{
                id: "hosAddressCancel",
                className: "button_secondary",
                text: "Cancel",
                callback: () => {
                    addressDialog.remove();
                }
            }, {
                id: "hosAddressOK",
                text: "OK",
                callback: () => {
                    let thisDialog = document.getElementById("hos-address-input");
                    if (!thisDialog) {
                        addressDialog.remove();
                        return;
                    }

                    this.hideAddressWarning();

                    let street = (<HTMLInputElement>thisDialog.querySelector("#tempaddress-street")).value;
                    let city = (<HTMLInputElement>thisDialog.querySelector("#tempaddress-city")).value;
                    let stateProvince = (<HTMLInputElement>thisDialog.querySelector("#tempaddress-state")).value;
                    let addressCountry = isEWD ? "Australia" : (<HTMLInputElement>thisDialog.querySelector("#tempaddress-country")).value;

                    let addressValid = this.validateAddressInput(street, city, stateProvince, addressCountry);

                    if (!addressValid) {
                        this.showAddressWarning();
                    } else {
                        if (log) {
                            let address = this.addToUserAddressCache(log, street, city, stateProvince, addressCountry);

                            // Temporarily set the address before the sync can download the proper coordinates
                            if (!hasKnownDevice(log) || log.malfunction === "Diagnostic" || log.malfunction === "Malfunction") {
                                // Clear location to make sure it's not populated with previously known coordinates
                                log.location = undefined;
                                log._address = address.formattedAddress;
                                log.editDateTime = getServerDate().toISOString();

                                updateNavigationView();

                                logger.log(logger.Source.UI, `Got new user inputted address for HOS log id = ${ log.id }, re-rendering page`, currentUser);
                                void this.sync.dutyStatusLogs.setItem(log).then(() => { populateChanges(); });
                            }
                        }

                        addressDialog.remove();
                    }
                }
            }],
            {
                isNightMode: isNightMode
            }
        );
        void addressDialog.open();
    }

    private showAddressWarning() {
        let element = (document.getElementById("hos-address-warning") as HTMLElement);
        element && show(element);
    }

    private hideAddressWarning() {
        let element = (document.getElementById("hos-address-warning") as HTMLElement);
        element && hide(element);
    }

    private addToUserAddressCache(log: DutyStatusLog, street: string, city: string, stateProvince: string, addressCountry: string): IAddress {
        let logId: string = log.id;
        let addressObject: IAddress = {
            city: city,
            stateProvince: stateProvince,
            country: addressCountry,
            formattedAddress: htmlEscape(this.formatAddress(street, city, stateProvince, addressCountry))
        };
        log.location = {
            address: {
                formattedAddress: this.formatAddress(street, city, stateProvince, addressCountry)
            }
        };
        // TODO: Implement reactive rendering
        let addressField = document.querySelector(`.reportItem[data-id="${ logId }"] span.hosAddress`);
        if (addressField) {
            addressField.innerHTML = `<span class="noTranslate">${ htmlEscape(addressObject.formattedAddress || "") }</span>`;
        }
        return addressObject;
    }

    private formatAddress(...addressParts: string[]) {
        return addressParts.filter(part => !!part).join(", ");
    }
}